import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { api, fmtCAD } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useI18n } from "@/context/I18nContext";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { t } = useI18n();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "" });
  const [loading, setLoading] = useState(false);

  if (items.length === 0) { nav("/panier"); return null; }
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error(t("checkout.name") + " / " + t("checkout.email")); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        customer: form, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error("Stripe", { description: err.response?.data?.detail || err.message });
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-12">
      <h1 className="font-heading text-4xl font-extrabold tracking-tight mb-10">{t("checkout.title")}</h1>
      <div className="grid lg:grid-cols-[1fr_360px] gap-10">
        <form onSubmit={submit} className="border border-gray-200 bg-white p-8 space-y-5" data-testid="checkout-form">
          <h2 className="font-heading text-xl font-bold">{t("checkout.info")}</h2>
          <div>
            <label className="block text-sm font-medium mb-1">{t("checkout.name")} *</label>
            <input value={form.name} onChange={set("name")} required className="w-full border border-gray-300 px-4 py-3 outline-none focus:border-brand-red" data-testid="checkout-name" />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium mb-1">{t("checkout.email")} *</label>
              <input type="email" value={form.email} onChange={set("email")} required className="w-full border border-gray-300 px-4 py-3 outline-none focus:border-brand-red" data-testid="checkout-email" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t("checkout.phone")}</label>
              <input value={form.phone} onChange={set("phone")} className="w-full border border-gray-300 px-4 py-3 outline-none focus:border-brand-red" data-testid="checkout-phone" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t("checkout.address")}</label>
            <input value={form.address} onChange={set("address")} className="w-full border border-gray-300 px-4 py-3 outline-none focus:border-brand-red" data-testid="checkout-address" />
          </div>
          <button type="submit" disabled={loading} className="w-full flex items-center justify-center gap-2 bg-brand-red text-white font-semibold py-4 hover:bg-[#b81c14] transition-colors disabled:opacity-60" data-testid="checkout-pay-btn">
            <Lock size={16} /> {loading ? t("checkout.redirect") : `${t("checkout.pay")} ${fmtCAD(subtotal)}`}
          </button>
          <p className="text-xs text-gray-400 text-center">{t("checkout.secure")}</p>
        </form>

        <aside className="border border-gray-200 bg-white p-6 h-fit">
          <h2 className="font-heading text-xl font-bold mb-5">{t("checkout.order")}</h2>
          <div className="space-y-3 max-h-72 overflow-auto">
            {items.map((it) => (
              <div key={it.product_id} className="flex gap-3 text-sm">
                <div className="h-12 w-12 bg-gray-50 border border-gray-200 shrink-0 flex items-center justify-center">
                  {it.image && <img src={it.image} alt="" className="max-h-full max-w-full object-contain p-1" />}
                </div>
                <div className="flex-1"><p className="line-clamp-1">{it.title}</p><p className="text-gray-400">x{it.quantity}</p></div>
                <span className="font-medium">{fmtCAD(it.price * it.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between py-4 mt-4 border-t border-gray-200 text-lg font-bold"><span>{t("cart.total")}</span><span>{fmtCAD(subtotal)}</span></div>
          <Link to="/panier" className="block text-center text-sm text-gray-500 hover:text-brand-red">{t("checkout.edit")}</Link>
        </aside>
      </div>
    </div>
  );
}
